import React, { useState, useEffect, useMemo } from 'react';
import { PlusIcon, CubeIcon, ExclamationTriangleIcon, CurrencyDollarIcon, ArrowDownTrayIcon, SparklesIcon } from '@heroicons/react/24/outline';
import { useProductosStore } from '../../stores/useProductosStore';
import { useCategoriasStore } from '../../stores/useCategoriasStore';
import { useAuthStore } from '../../stores/useAuthStore';
import { useCompanyStore } from '../../stores/useCompanyStore';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import ListaProductos, { type CupoHorario } from './ListaProductos';
import FiltrosProductos, {
  FILTRO_ESTADO_STOCK_BAJO,
  STOCK_URL_AGOTADO,
  STOCK_URL_BAJO,
} from './FiltrosProductos';
import ModalProducto from './ModalProducto';
import PaquetesSection from './PaquetesSection';
import { Producto } from '../../types';
import { ProductExportService } from '../../services/ProductExportService';
import { logger } from '../../utils/logger';
import { useEmpresaRolePermissions } from '../../hooks/useEmpresaRolePermissions';
import {
  fetchProductoVentasStatsMap,
  rotacionInventarioRef,
  type ProductoVentaStats,
} from '../../utils/productoVentasStats';
import { GestionSuscripciones } from '../../pages/suscripciones/GestionSuscripciones';
import { ColeccionPickerModal } from '../colecciones/ColeccionPickerModal';
import { useColeccionesStore } from '../../stores/useColeccionesStore';
import { buildPublicCategoriaUrl } from '../../utils/catalogPublicUrls';
import { atributosRowsToCatalogRpcJson, normalizeAtributosFromDb } from '../../utils/productoAtributos';
import { isProductoArchivado } from '../../utils/productoEstado';

/** Producto tipo servicio: sin stock contable (misma regla que ListaProductos). */
function isProductoServicio(p: Producto): boolean {
  return (p as { inventory_type?: string }).inventory_type === 'unlimited';
}

/** Agotado = stock 0 (inventario tracked). Alineado con alertas digest. */
function isAgotadoReorder(p: Producto): boolean {
  if (isProductoServicio(p)) return false;
  const stock = Number(p.stock ?? 0);
  return !Number.isNaN(stock) && stock === 0;
}

/** Stock bajo = por debajo del mínimo pero aún con unidades (planificar reorden). */
function isStockBajoReorder(p: Producto): boolean {
  if (isProductoServicio(p)) return false;
  const stock = Number(p.stock ?? 0);
  const min =
    Number(p.stockMinimo ?? (p as { stock_minimo?: number }).stock_minimo ?? 0);
  if (Number.isNaN(stock) || Number.isNaN(min) || min <= 0) return false;
  return stock > 0 && stock <= min;
}

function tipoOfertaCatalogo(p: Producto): 'producto' | 'servicio' | 'membresia' | 'listado' {
  const raw =
    (p as { tipoOferta?: string; tipo_oferta?: string }).tipoOferta ??
    (p as { tipo_oferta?: string }).tipo_oferta;
  if (raw === 'servicio' || raw === 'membresia' || raw === 'listado' || raw === 'producto') return raw;
  if (p.esSuscripcion) return 'membresia';
  if (isProductoServicio(p)) return 'servicio';
  return 'producto';
}

/** Stock para ordenar: servicios al final en ascendente (como “sin tope”). */
function stockParaOrden(p: Producto): number {
  if ((p as { inventory_type?: string }).inventory_type === 'unlimited') {
    return Number.POSITIVE_INFINITY;
  }
  return Number(p.stock ?? 0);
}

function compareProductosOrden(
  a: Producto,
  b: Producto,
  ordenarPor: string,
  descendente: boolean,
  stats: Record<string, ProductoVentaStats>
): number {
  const dir = descendente ? -1 : 1;
  const byNombre = () =>
    (a.nombre || '').localeCompare(b.nombre || '', 'es', { sensitivity: 'base' });
  const cmpNum = (x: number, y: number): number => {
    if (x < y) return -dir;
    if (x > y) return dir;
    return 0;
  };

  let primary = 0;

  switch (ordenarPor) {
    case 'nombre': {
      const r = byNombre();
      return descendente ? -r : r;
    }
    case 'precioVenta':
      primary = cmpNum(a.precioVenta ?? 0, b.precioVenta ?? 0);
      break;
    case 'margenBruto':
      primary = cmpNum(a.margenBruto ?? 0, b.margenBruto ?? 0);
      break;
    case 'stock':
      primary = cmpNum(stockParaOrden(a), stockParaOrden(b));
      break;
    case 'ventasUltimos30Dias': {
      const ua = stats[a.id]?.unidades12m ?? a.ventasUltimos30Dias ?? 0;
      const ub = stats[b.id]?.unidades12m ?? b.ventasUltimos30Dias ?? 0;
      primary = cmpNum(ua, ub);
      break;
    }
    case 'rentabilidadUltimos30Dias': {
      const pa = (stats[a.id]?.montoVendido12m ?? 0) * (a.margenBruto ?? 0);
      const pb = (stats[b.id]?.montoVendido12m ?? 0) * (b.margenBruto ?? 0);
      primary = cmpNum(pa, pb);
      break;
    }
    case 'rotacionInventario': {
      const rotVal = (p: Producto) => {
        if ((p as { inventory_type?: string }).inventory_type === 'unlimited') return -1;
        const u = stats[p.id]?.unidades12m ?? 0;
        const s = Number(p.stock ?? 0);
        const r = rotacionInventarioRef(u, s);
        if (r === null) {
          if (u > 0 && s <= 0) return Number.POSITIVE_INFINITY;
          return 0;
        }
        return r;
      };
      primary = cmpNum(rotVal(a), rotVal(b));
      break;
    }
    case 'fechaUltimaCompra': {
      const ms = (p: Producto) => {
        const d = p.fechaUltimaCompra;
        if (d == null) return null;
        const t = d instanceof Date ? d.getTime() : new Date(d as string).getTime();
        return Number.isFinite(t) ? t : null;
      };
      const ta = ms(a);
      const tb = ms(b);
      if (ta == null && tb == null) primary = 0;
      else if (ta == null) primary = dir;
      else if (tb == null) primary = -dir;
      else primary = cmpNum(ta, tb);
      break;
    }
    case 'fechaCreacion': {
      const ta = a.createdAt instanceof Date ? a.createdAt.getTime() : new Date(a.createdAt).getTime();
      const tb = b.createdAt instanceof Date ? b.createdAt.getTime() : new Date(b.createdAt).getTime();
      primary = cmpNum(
        Number.isFinite(ta) ? ta : 0,
        Number.isFinite(tb) ? tb : 0
      );
      break;
    }
    default: {
      const r = byNombre();
      return descendente ? -r : r;
    }
  }

  if (primary !== 0) return primary;
  const tie = byNombre();
  return descendente ? -tie : tie;
}

type ProductosTab = 'productos' | 'servicios' | 'listados' | 'paquetes' | 'membresias' | 'archivo';

const PRODUCTOS_TABS: { id: ProductosTab; label: string }[] = [
  { id: 'productos', label: 'Productos' },
  { id: 'servicios', label: 'Servicios' },
  { id: 'listados', label: 'Listados' },
  { id: 'paquetes', label: 'Paquetes' },
  { id: 'membresias', label: 'Membresías' },
  { id: 'archivo', label: 'Archivo' },
];

function tabFromSearchParams(sp: URLSearchParams): ProductosTab {
  const t = sp.get('tab');
  if (
    t === 'servicios' ||
    t === 'listados' ||
    t === 'paquetes' ||
    t === 'membresias' ||
    t === 'archivo' ||
    t === 'productos'
  )
    return t;
  return 'productos';
}

const GestionProductos: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { productos, isLoading, error, fetchProductos, crearProducto, actualizarProducto, actualizarStockEnLinea, archivarProducto, restaurarProducto, clearError } = useProductosStore();
  const { usuario } = useAuthStore();
  const { empresas, currentEmpresa, loading: companiesLoading, fetchUserEmpresas } = useCompanyStore();
  const { categorias } = useCategoriasStore();
  const { canSeeCosto, canEditProducto } = useEmpresaRolePermissions();
  const [mostrarModal, setMostrarModal] = useState(false);
  const [isSavingProduct, setIsSavingProduct] = useState(false);
  const [productoSeleccionado, setProductoSeleccionado] = useState<Producto | null>(null);
  const [modoModal, setModoModal] = useState<'crear' | 'editar'>('crear');
  const [selectedProductIds, setSelectedProductIds] = useState<Set<string>>(new Set());
  const [selectionMode, setSelectionMode] = useState(false);
  const [coleccionPickerOpen, setColeccionPickerOpen] = useState(false);
  const [filtros, setFiltros] = useState({
    busqueda: '',
    categoria: '',
    estado: '',
    ordenarPor: 'nombre',
    ordenDescendente: false
  });
  const [tab, setTab] = useState<ProductosTab>(() => tabFromSearchParams(searchParams));

  useEffect(() => {
    setTab(tabFromSearchParams(searchParams));
  }, [searchParams]);

  /** Deep-link desde alertas: /productos?stock=agotado|bajo */
  useEffect(() => {
    const stock = searchParams.get('stock');
    if (stock === STOCK_URL_AGOTADO) {
      setFiltros((f) => ({ ...f, estado: 'AGOTADO' }));
    } else if (stock === STOCK_URL_BAJO) {
      setFiltros((f) => ({ ...f, estado: FILTRO_ESTADO_STOCK_BAJO }));
    }
  }, [searchParams]);

  const setProductosTab = (next: ProductosTab) => {
    setTab(next);
    setSearchParams(
      (prev) => {
        const n = new URLSearchParams(prev);
        if (next === 'productos') n.delete('tab');
        else n.set('tab', next);
        return n;
      },
      { replace: true }
    );
  };

  useEffect(() => {
    setSelectedProductIds(new Set());
    setSelectionMode(false);
    setColeccionPickerOpen(false);
  }, [tab]);
  const [ventasStatsPorProducto, setVentasStatsPorProducto] = useState<
    Record<string, ProductoVentaStats>
  >({});
  const [ventasStatsLoading, setVentasStatsLoading] = useState(false);
  const [cupoStats, setCupoStats] = useState<Record<string, CupoHorario[]>>({});
  const [membresiaActivos, setMembresiaActivos] = useState<Record<string, number>>({});

  const logSupabaseError = (label: string, error: unknown) => {
    const e = error as { message?: string; details?: string; code?: string; hint?: string } | null;
    logger.error(label, {
      message: e?.message,
      details: e?.details,
      code: e?.code,
      hint: e?.hint,
      raw: error,
    });
    // Keep a plain console log for quick browser debugging
    console.error(`${label}:`, {
      message: e?.message,
      details: e?.details,
      code: e?.code,
      hint: e?.hint,
      raw: error,
    });
  };

  // Fetch user empresas if not loaded
  useEffect(() => {
    if (usuario?.id && empresas.length === 0 && !companiesLoading) {
      logger.debug('🔧 GestionProductos: Fetching empresas for user:', usuario.id);
      fetchUserEmpresas(usuario.id);
    }
  }, [usuario?.id, empresas.length, companiesLoading, fetchUserEmpresas]);

  // Solo depender de empresa: las acciones de Zustand no deben ir en el array (pueden provocar bucles de refetch).
  useEffect(() => {
    if (!currentEmpresa?.id) return;
    logger.debug('✅ GestionProductos: Current empresa found:', currentEmpresa.id);
    const { fetchProductos: fp } = useProductosStore.getState();
    const { fetchCategorias: fc } = useCategoriasStore.getState();
    void fp(currentEmpresa.id);
    void fc();
  }, [currentEmpresa?.id]);

  useEffect(() => {
    if (!currentEmpresa?.id) return;
    if (tab !== 'productos' && tab !== 'servicios' && tab !== 'membresias' && tab !== 'listados') return;
    let cancelled = false;
    setVentasStatsLoading(true);
    fetchProductoVentasStatsMap(currentEmpresa.id)
      .then((m) => {
        if (!cancelled) setVentasStatsPorProducto(m);
      })
      .catch(() => {
        if (!cancelled) setVentasStatsPorProducto({});
      })
      .finally(() => {
        if (!cancelled) setVentasStatsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [currentEmpresa?.id, tab]);

  useEffect(() => {
    if (!currentEmpresa?.id) {
      setCupoStats({});
      setMembresiaActivos({});
      return;
    }
    if (tab !== 'servicios' && tab !== 'membresias') {
      setCupoStats({});
      setMembresiaActivos({});
      return;
    }
    let cancelled = false;
    void supabase
      .from('v_horario_cupo')
      .select('*')
      .eq('empresa_id', currentEmpresa.id)
      .then(({ data, error }) => {
        if (cancelled || error || !data) return;
        const grouped: Record<string, CupoHorario[]> = {};
        for (const row of data as Record<string, unknown>[]) {
          const pid = String(row.producto_id);
          if (!grouped[pid]) grouped[pid] = [];
          const bucket = grouped[pid]!;
          const hi = row.hora_inicio != null ? String(row.hora_inicio).slice(0, 5) : '';
          const hf = row.hora_fin != null ? String(row.hora_fin).slice(0, 5) : '';
          bucket.push({
            horario_id: String(row.horario_id),
            producto_id: pid,
            tipo_slot: String(row.tipo_slot ?? ''),
            dia_semana: String(row.dia_semana ?? ''),
            hora_inicio: hi,
            hora_fin: hf,
            capacidad_maxima:
              row.capacidad_maxima != null && row.capacidad_maxima !== ''
                ? Number(row.capacidad_maxima)
                : null,
            cupo_actual: Number(row.cupo_actual ?? 0) || 0,
            cupo_disponible:
              row.cupo_disponible != null && row.cupo_disponible !== ''
                ? Number(row.cupo_disponible)
                : null,
            requiere_staff: Boolean(row.requiere_staff),
          });
        }
        setCupoStats(grouped);
      });
    void supabase
      .from('cliente_membresias')
      .select('producto_id')
      .eq('empresa_id', currentEmpresa.id)
      .eq('estado', 'activa')
      .then(({ data, error }) => {
        if (cancelled || error || !data) return;
        const m: Record<string, number> = {};
        for (const r of data as { producto_id: string }[]) {
          m[r.producto_id] = (m[r.producto_id] ?? 0) + 1;
        }
        setMembresiaActivos(m);
      });
    return () => {
      cancelled = true;
    };
  }, [currentEmpresa?.id, tab]);

  // Auto-clear error after 10 seconds
  useEffect(() => {
    if (error) {
      const timer = setTimeout(() => {
        clearError();
      }, 10000);
      return () => clearTimeout(timer);
    }
    return undefined;
  }, [error, clearError]);

  const handleCrearProducto = () => {
    setProductoSeleccionado(null);
    setModoModal('crear');
    setMostrarModal(true);
  };

  const handleEditarProducto = (producto: Producto) => {
    setProductoSeleccionado(producto);
    setModoModal('editar');
    setMostrarModal(true);
  };

  const handleGuardarProducto = async (datosProducto: Partial<Producto>, options?: { pendingImageFiles?: File[] }) => {
    if (!canEditProducto) {
      logger.warn('GestionProductos: save blocked by role');
      return;
    }
    if (isSavingProduct) {
      logger.warn('GestionProductos: save skipped (already saving)');
      return;
    }
    setIsSavingProduct(true);
    try {
      logger.debug('🔄 GestionProductos: Starting to save product:', datosProducto);
      logger.debug('🖼️ GestionProductos: Product imagen field:', datosProducto.imagen);
      logger.debug('🖼️ GestionProductos: Has imagen?', !!datosProducto.imagen);
      
      if (!currentEmpresa?.id) {
        alert('No se encontró empresa. Por favor, crea una empresa primero.');
        return;
      }
      
      let productId: string | null = null;
      
      if (modoModal === 'crear') {
        logger.debug('🔄 GestionProductos: Creating new product...');
        const newProduct = await crearProducto(datosProducto, currentEmpresa.id);
        productId = newProduct?.id || null;
        logger.debug('✅ GestionProductos: Product created successfully', productId);
        if (productId && options?.pendingImageFiles?.length && currentEmpresa.id) {
          const { uploadProductImages } = useProductosStore.getState();
          try {
            await uploadProductImages(productId, options.pendingImageFiles);
          } catch (uploadErr) {
            logger.error('Error uploading product images after create:', uploadErr);
          }
        }
      } else if (productoSeleccionado) {
        logger.debug('🔄 GestionProductos: Updating existing product...');
        await actualizarProducto(productoSeleccionado.id, datosProducto, currentEmpresa.id);
        productId = productoSeleccionado.id;
        logger.debug('✅ GestionProductos: Product updated successfully');
      }
      
      // Save bookable_items configuration if product has scheduling settings
      const isBookable = (datosProducto as any).isBookable;
      if (productId && currentEmpresa.id && isBookable !== undefined) {
        try {
          const bookableData = {
            empresa_id: currentEmpresa.id,
            item_type: 'producto',
            item_id: productId,
            is_bookable: isBookable || false,
            requires_time_slot: (datosProducto as any).requiresTimeSlot !== false,
            custom_duration_minutes: (datosProducto as any).customDurationMinutes || null,
            fulfillment_options: (datosProducto as any).fulfillmentOptions || ['pickup'],
          };

          const { error: bookableError } = await supabase
            .from('bookable_items')
            .upsert(bookableData, {
              onConflict: 'empresa_id,item_type,item_id'
            });

          if (bookableError) {
            logger.error('Error saving bookable item config:', bookableError);
          } else {
            logger.debug('✅ Bookable item config saved successfully');
          }
        } catch (bookableErr) {
          logger.error('Error saving bookable item config:', bookableErr);
        }
      }

      const coleccionIds = (datosProducto as { _coleccionIds?: string[] })._coleccionIds;
      if (productId && coleccionIds !== undefined) {
        try {
          const { setProductoColecciones } = useColeccionesStore.getState();
          await setProductoColecciones(productId, coleccionIds);
        } catch (colErr) {
          logger.error('Colecciones al guardar producto:', colErr);
        }
      }

      if (productId && currentEmpresa?.id) {
        try {
          const catalogMeta = atributosRowsToCatalogRpcJson(
            normalizeAtributosFromDb((datosProducto as { atributos?: unknown }).atributos)
          );
          if (catalogMeta.length > 0) {
            const { error: catErr } = await supabase.rpc('upsert_atributos_catalogo', {
              p_empresa_id: currentEmpresa.id,
              p_atributos: catalogMeta,
            });
            if (catErr) {
              logger.error('upsert_atributos_catalogo', catErr);
            }
          }
        } catch (catE) {
          logger.error('Catálogo de atributos:', catE);
        }
      }

      if (productId && currentEmpresa?.id) {
        try {
          const { error: delErr } = await supabase
            .from('oferta_horarios')
            .delete()
            .eq('producto_id', productId);
          if (delErr) logSupabaseError('Error clearing oferta_horarios', delErr);

          const horariosToSave = (datosProducto as { horarios?: unknown }).horarios as
            | Array<Record<string, unknown> & { tempId?: string }>
            | undefined;
          if (horariosToSave?.length) {
            const toTime = (t: string | undefined | null) => {
              if (!t || typeof t !== 'string') return null;
              return t.length === 5 ? `${t}:00` : t;
            };
            const baseRows = horariosToSave.map(({ tempId: _tid, ...h }) => ({
              empresa_id: currentEmpresa.id,
              producto_id: productId,
              tipo_slot: h.tipo_slot,
              dia_semana: h.dia_semana,
              hora_inicio: toTime(h.hora_inicio as string),
              hora_fin: toTime(h.hora_fin as string),
              capacidad_maxima: h.capacidad_maxima ?? null,
              modalidad: h.modalidad ?? 'presencial',
              ubicacion: (h.ubicacion as string) || null,
              activo: true,
            }));

            const rowsWithStaff = horariosToSave.map(({ tempId: _tid, ...h }) => ({
              empresa_id: currentEmpresa.id,
              producto_id: productId,
              tipo_slot: h.tipo_slot,
              dia_semana: h.dia_semana,
              hora_inicio: toTime(h.hora_inicio as string),
              hora_fin: toTime(h.hora_fin as string),
              capacidad_maxima: h.capacidad_maxima ?? null,
              modalidad: h.modalidad ?? 'presencial',
              ubicacion: (h.ubicacion as string) || null,
              requiere_staff: Boolean(h.requiere_staff),
              activo: true,
            }));

            let { error: horariosError } = await supabase
              .from('oferta_horarios')
              .insert(rowsWithStaff);

            // Backward-compat: if DB schema still lacks `requiere_staff`, retry without it.
            if (
              horariosError &&
              typeof horariosError.message === 'string' &&
              horariosError.message.toLowerCase().includes('requiere_staff')
            ) {
              const retry = await supabase
                .from('oferta_horarios')
                .insert(baseRows as Record<string, unknown>[]);
              horariosError = retry.error;
            }

            if (horariosError) {
              logSupabaseError('Error saving oferta_horarios', horariosError);
              alert('Se guardó el producto, pero no se pudieron guardar los horarios. Revisa permisos/migraciones de oferta_horarios.');
            }
          }
        } catch (err) {
          logSupabaseError('Error saving oferta_horarios (catch)', err);
          alert('Se guardó el producto, pero falló el guardado de horarios.');
        }
      }

      setMostrarModal(false);
      
      logger.debug('🔄 GestionProductos: Fetching products after save...');
      await fetchProductos(currentEmpresa.id);
      logger.debug('✅ GestionProductos: Products fetched, current count:', productos.length);
      
    } catch (error) {
      logger.error('❌ GestionProductos: Error al guardar producto:', error);
      logSupabaseError('GestionProductos save error', error);
    } finally {
      setIsSavingProduct(false);
    }
  };

  const handleDuplicarProducto = async (producto: Producto) => {
    if (!canEditProducto || !currentEmpresa?.id) return;
    try {
      const { data, error } = await supabase.rpc('duplicate_oferta', { p_oferta_id: producto.id });
      if (error) {
        window.alert(error.message || 'No se pudo duplicar');
        return;
      }
      const newId = data != null ? String(data).trim() : '';
      if (!newId) {
        window.alert('No se recibió el id del duplicado');
        return;
      }
      await fetchProductos(currentEmpresa.id);
      const lista = useProductosStore.getState().productos;
      const fresh = lista.find((p) => p.id === newId);
      if (fresh) {
        setProductoSeleccionado(fresh);
        setModoModal('editar');
        setMostrarModal(true);
      } else {
        window.alert('Producto duplicado. Actualiza la lista si no lo ves.');
      }
    } catch (e) {
      logger.error('Duplicar producto:', e);
      window.alert(e instanceof Error ? e.message : 'Error al duplicar');
    }
  };

  const handleArchivarProducto = async (id: string) => {
    if (!canEditProducto) return;
    if (
      window.confirm(
        '¿Archivar este producto? No aparecerá en el inventario activo ni en ventas nuevas. Las facturas existentes se conservan.'
      )
    ) {
      try {
        await archivarProducto(id);
        if (currentEmpresa?.id) {
          await fetchProductos(currentEmpresa.id);
        }
      } catch (err) {
        logger.error('Error al archivar producto:', err);
      }
    }
  };

  const handleRestaurarProducto = async (id: string) => {
    if (!canEditProducto) return;
    if (
      window.confirm(
        '¿Restaurar este producto al inventario activo? Volverá a estar disponible según su estado y stock.'
      )
    ) {
      try {
        await restaurarProducto(id);
        if (currentEmpresa?.id) {
          await fetchProductos(currentEmpresa.id);
        }
      } catch (err) {
        logger.error('Error al restaurar producto:', err);
      }
    }
  };

  const listaBaseInventario = useMemo(() => {
    if (tab === 'archivo') {
      return productos.filter((p) => isProductoArchivado(p));
    }
    const activos = productos.filter((p) => !isProductoArchivado(p));
    if (tab === 'productos') {
      return activos.filter((p) => tipoOfertaCatalogo(p) === 'producto');
    }
    if (tab === 'servicios') {
      return activos.filter((p) => tipoOfertaCatalogo(p) === 'servicio');
    }
    if (tab === 'membresias') {
      return activos.filter((p) => tipoOfertaCatalogo(p) === 'membresia');
    }
    if (tab === 'listados') {
      return activos.filter((p) => tipoOfertaCatalogo(p) === 'listado');
    }
    return [];
  }, [productos, tab]);

  // Filtrar productos según los filtros aplicados (solo pestañas inventario / servicios)
  const productosFiltrados = useMemo(() => {
    const stockUrl = searchParams.get('stock');
    return listaBaseInventario.filter((producto) => {
      const cumpleBusqueda =
        !filtros.busqueda ||
        producto.nombre.toLowerCase().includes(filtros.busqueda.toLowerCase()) ||
        producto.sku.toLowerCase().includes(filtros.busqueda.toLowerCase()) ||
        producto.descripcion?.toLowerCase().includes(filtros.busqueda.toLowerCase());

      const cumpleCategoria = !filtros.categoria || producto.categoria === filtros.categoria;
      const cumpleStockUrl = (() => {
        if (stockUrl === STOCK_URL_AGOTADO) return isAgotadoReorder(producto);
        if (stockUrl === STOCK_URL_BAJO) return isStockBajoReorder(producto);
        return true;
      })();
      const cumpleEstado = (() => {
        if (stockUrl === STOCK_URL_AGOTADO || stockUrl === STOCK_URL_BAJO) return true;
        if (!filtros.estado) return true;
        if (filtros.estado === FILTRO_ESTADO_STOCK_BAJO) return isStockBajoReorder(producto);
        if (filtros.estado === 'AGOTADO') return isAgotadoReorder(producto);
        return producto.estado === filtros.estado;
      })();

      return cumpleBusqueda && cumpleCategoria && cumpleStockUrl && cumpleEstado;
    });
  }, [listaBaseInventario, filtros, searchParams]);

  const categoriaFiltroDetalle = useMemo(() => {
    if (!filtros.categoria.trim()) return undefined;
    return categorias.find((c) => c.nombre === filtros.categoria);
  }, [categorias, filtros.categoria]);

  const empresaSlugProductos = (currentEmpresa?.slug || '').trim();

  const productosOrdenados = useMemo(() => {
    const list = [...productosFiltrados];
    list.sort((a, b) =>
      compareProductosOrden(
        a,
        b,
        filtros.ordenarPor,
        filtros.ordenDescendente,
        ventasStatsPorProducto
      )
    );
    return list;
  }, [
    productosFiltrados,
    filtros.ordenarPor,
    filtros.ordenDescendente,
    ventasStatsPorProducto,
  ]);

  const exportEmptyMessage = () => {
    if (tab === 'archivo') return 'No hay productos archivados';
    if (tab === 'servicios') return 'No hay servicios para exportar';
    if (tab === 'listados') return 'No hay listados para exportar';
    if (tab === 'membresias') return 'No hay planes de membresía para exportar';
    return 'No hay productos para exportar';
  };

  const handleExportarCSV = () => {
    if (productosOrdenados.length === 0) {
      alert(exportEmptyMessage());
      return;
    }
    const fecha = new Date().toISOString().split('T')[0];
    ProductExportService.exportToCSV(productosOrdenados, `inventario-${fecha}.csv`, {
      includeCostColumns: canSeeCosto,
    });
  };

  const inventarioActivo =
    tab === 'productos' ||
    tab === 'servicios' ||
    tab === 'listados' ||
    tab === 'membresias' ||
    tab === 'archivo';

  const tipoVistaLista: 'producto' | 'servicio' | 'membresia' | 'listado' =
    tab === 'servicios' ? 'servicio' : tab === 'membresias' ? 'membresia' : tab === 'listados' ? 'listado' : 'producto';

  // Debug info
  logger.debug('🔍 Products Debug:', {
    isLoading,
    productosCount: productos.length,
    listaBaseInventarioCount: listaBaseInventario.length,
    productosFiltradosCount: productosFiltrados.length,
    filtrosActivos: filtros,
    error
  });

  if (isLoading || companiesLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  // Show helpful message if user has no company (only after loading is complete)
  // Also check if currentEmpresa exists, as user might have empresas but no currentEmpresa set
  if (!companiesLoading && empresas.length === 0 && !currentEmpresa) {
    return (
      <div className="space-y-6">
        <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-lg p-8 text-center">
          <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-blue-100 mb-4">
            <svg className="h-6 w-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <h3 className="text-lg font-semibold text-blue-900 mb-2">
            ¡Crea tu primera empresa! 🏢
          </h3>
          <p className="text-blue-800 mb-6 max-w-md mx-auto">
            Para gestionar productos, necesitas crear una empresa primero. 
            Ve al dashboard y haz clic en "Nueva Empresa" para comenzar.
          </p>
          <button
            onClick={() => navigate('/dashboard')}
            className="inline-flex items-center px-6 py-3 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700 focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors"
          >
            <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
            </svg>
            Ir al Dashboard
          </button>
        </div>
      </div>
    );
  }

  const showSelectionBar =
    inventarioActivo && tab !== 'archivo' && canEditProducto && selectionMode && selectedProductIds.size > 0;

  const subtituloProductos = (() => {
    if (tab === 'paquetes') return 'Combos y paquetes';
    const n = listaBaseInventario.length;
    if (tab === 'servicios') return `${n} servicio${n === 1 ? '' : 's'}`;
    if (tab === 'listados') return `${n} listado${n === 1 ? '' : 's'}`;
    if (tab === 'membresias') return `${n} membresía${n === 1 ? '' : 's'}`;
    if (tab === 'archivo') return `${n} archivado${n === 1 ? '' : 's'}`;
    return `${n} producto${n === 1 ? '' : 's'}`;
  })();

  return (
    <div className={`space-y-6 -mx-4 sm:-mx-6 lg:-mx-8 -my-6 min-h-[calc(100vh-4rem)] bg-[#F4F6FB] px-4 sm:px-6 lg:px-8 py-6 ${showSelectionBar ? 'pb-28' : ''}`}>

      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-[#0B1F4A]">Productos</h1>
          <p className="mt-1 text-sm text-gray-500">{subtituloProductos}</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {inventarioActivo && tab !== 'archivo' && canEditProducto && (
            <button
              type="button"
              onClick={() => {
                setSelectionMode((m) => {
                  if (m) setSelectedProductIds(new Set());
                  return !m;
                });
              }}
              className={`inline-flex items-center rounded-full px-3 py-2 text-sm font-medium transition-colors ${
                selectionMode
                  ? 'bg-[#0B1F4A] text-white'
                  : 'text-gray-500 hover:text-[#0B1F4A] hover:bg-white'
              }`}
            >
              {selectionMode ? 'Cancelar selección' : 'Seleccionar'}
            </button>
          )}
          <button
            type="button"
            onClick={handleExportarCSV}
            disabled={!inventarioActivo || productosOrdenados.length === 0}
            className="inline-flex items-center rounded-full border border-gray-200 bg-white px-4 py-2 text-sm font-semibold text-[#0B1F4A] shadow-sm hover:bg-gray-50 disabled:opacity-50"
          >
            Exportar
          </button>
          {inventarioActivo && tab !== 'archivo' && canEditProducto && (
            <button
              type="button"
              onClick={() => navigate('/productos/estudio')}
              className="inline-flex items-center px-3 py-2 border border-indigo-200 rounded-md shadow-sm text-sm font-medium text-indigo-800 bg-indigo-50 hover:bg-indigo-100"
              title="Centro de Diseño: imágenes de producto y plantillas para Tus Redes"
            >
              <SparklesIcon className="w-4 h-4 mr-2" />
              Centro de Diseño
            </button>
          )}
          {inventarioActivo && tab !== 'archivo' && canEditProducto && (
            <button
              onClick={handleCrearProducto}
              className="inline-flex items-center px-4 py-2 rounded-full shadow-sm text-sm font-semibold text-white bg-[#1A56DB] hover:bg-[#1648c7]"
            >
              <PlusIcon className="w-4 h-4 mr-1.5" />
              Nuevo producto
            </button>
          )}
        </div>
      </div>

      <div className="flex flex-wrap gap-1.5">
        {PRODUCTOS_TABS.map((t) => (
          <button
            key={t.id}
            type="button"
            onClick={() => setProductosTab(t.id)}
            className={`inline-flex items-center rounded-full px-3.5 py-1.5 text-sm font-medium transition-colors ${
              tab === t.id
                ? 'bg-[#0B1F4A] text-white shadow-sm'
                : 'text-gray-500 hover:text-[#0B1F4A] hover:bg-white/80'
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {tab === 'paquetes' ? (
        <PaquetesSection />
      ) : (
        <>
          {tab === 'archivo' && (
            <div className="rounded-2xl border border-gray-100 bg-white px-4 py-3 text-sm text-gray-600">
              Productos que ya no vendes. Siguen vinculados a facturas y reportes históricos. Puedes{' '}
              <strong>restaurar</strong> uno para volver al inventario activo.
            </div>
          )}

          <div className="space-y-6">
          <FiltrosProductos
            filtros={filtros}
            ocultarEstado={tab === 'archivo'}
            onFiltrosChange={(newFiltros) => {
              logger.debug('🔍 GestionProductos: Filter change received:', newFiltros);
              setFiltros(newFiltros);
            }}
          />

          {filtros.categoria.trim() !== '' && (
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between px-4 py-3 bg-blue-50 border border-blue-100 rounded-lg">
              <p className="text-sm text-blue-900">
                Mostrando:{' '}
                <strong>{categoriaFiltroDetalle?.nombre ?? filtros.categoria}</strong>
              </p>
              {categoriaFiltroDetalle && empresaSlugProductos ? (
                <div className="flex flex-wrap items-center gap-2">
                  <button
                    type="button"
                    onClick={async () => {
                      const url = buildPublicCategoriaUrl(
                        window.location.origin,
                        empresaSlugProductos,
                        categoriaFiltroDetalle
                      );
                      try {
                        await navigator.clipboard.writeText(url);
                        const el = document.createElement('div');
                        el.setAttribute('role', 'status');
                        el.className =
                          'fixed bottom-6 left-1/2 z-[9999] -translate-x-1/2 rounded-lg bg-[#0B1F4A] px-4 py-2.5 text-sm text-white shadow-lg';
                        el.textContent = 'Enlace público copiado';
                        document.body.appendChild(el);
                        window.setTimeout(() => el.remove(), 3200);
                      } catch {
                        window.prompt('Copia este enlace:', url);
                      }
                    }}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-[#1A56DB] bg-white rounded-lg border border-blue-200 hover:bg-blue-100"
                  >
                    Copiar enlace público
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      const url = buildPublicCategoriaUrl(
                        window.location.origin,
                        empresaSlugProductos,
                        categoriaFiltroDetalle
                      );
                      const text = `¡Mira nuestros productos de ${categoriaFiltroDetalle.nombre}!\n${url}`;
                      window.open(
                        `https://wa.me/?text=${encodeURIComponent(text)}`,
                        '_blank',
                        'noopener,noreferrer'
                      );
                    }}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-green-800 bg-white rounded-lg border border-green-200 hover:bg-green-50"
                  >
                    Compartir
                  </button>
                </div>
              ) : (
                <p className="text-xs text-amber-800 sm:max-w-md sm:text-right">
                  {categoriaFiltroDetalle && !empresaSlugProductos
                    ? 'Configura el slug público de la empresa para generar el enlace de esta categoría.'
                    : null}
                </p>
              )}
            </div>
          )}

          <ListaProductos
            productos={productosOrdenados}
            canSeeCosto={canSeeCosto}
            canEditProducto={canEditProducto}
            onEditar={handleEditarProducto}
            onArchivar={tab !== 'archivo' ? handleArchivarProducto : undefined}
            onRestaurar={tab === 'archivo' ? handleRestaurarProducto : undefined}
            vistaArchivo={tab === 'archivo'}
            onActualizarStockInventario={
              canEditProducto && currentEmpresa?.id && tab !== 'archivo'
                ? (id, stock, min) => actualizarStockEnLinea(id, stock, min, currentEmpresa.id)
                : undefined
            }
            selectedIds={selectedProductIds}
            onSelectOne={
              selectionMode
                ? (id) => {
                    setSelectedProductIds((prev) => {
                      const next = new Set(prev);
                      if (next.has(id)) next.delete(id);
                      else next.add(id);
                      return next;
                    });
                  }
                : undefined
            }
            onSelectAll={
              selectionMode
                ? (checked) => {
                    setSelectedProductIds(checked ? new Set(productosOrdenados.map((p) => p.id)) : new Set());
                  }
                : undefined
            }
            ventasStatsPorProducto={ventasStatsPorProducto}
            ventasStatsLoading={ventasStatsLoading}
            tipoVista={tipoVistaLista}
            cupoStats={cupoStats}
            membresiaActivos={membresiaActivos}
            onDuplicar={canEditProducto && tab !== 'archivo' ? handleDuplicarProducto : undefined}
          />

          {tab === 'membresias' && (
            <div className="rounded-2xl border border-gray-100 bg-white p-4 sm:p-6 mt-6">
              <p className="text-sm text-gray-600 mb-4">
                Suscripciones y membresías activas de tus clientes (cobros y periodos).
              </p>
              <GestionSuscripciones embed empresaId={currentEmpresa?.id} />
            </div>
          )}
          </div>

          {mostrarModal && (
            <ModalProducto
              producto={productoSeleccionado}
              modo={modoModal}
              onGuardar={handleGuardarProducto}
              onCancelar={() => setMostrarModal(false)}
              onDuplicar={canEditProducto ? handleDuplicarProducto : undefined}
            />
          )}
        </>
      )}

      {/* Mensaje de error */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-md p-4">
          <div className="flex">
            <div className="flex-shrink-0">
              <span className="text-red-400">⚠️</span>
            </div>
            <div className="ml-3 flex-1">
              <h3 className="text-sm font-medium text-red-800">Error</h3>
              <p className="text-sm text-red-700 mt-1">{error}</p>
            </div>
            <div className="flex-shrink-0 ml-3 space-x-2">
              <button
                onClick={() => {
                  clearError();
                  if (currentEmpresa?.id) {
                    fetchProductos(currentEmpresa.id);
                  }
                }}
                className="text-blue-600 hover:text-blue-800 text-sm font-medium underline"
              >
                Reintentar
              </button>
              <button
                onClick={clearError}
                className="text-red-600 hover:text-red-800 text-sm font-medium underline"
              >
                Cerrar
              </button>
            </div>
          </div>
        </div>
      )}

      {showSelectionBar && currentEmpresa?.id && (
        <div
          className="fixed bottom-0 left-0 right-0 z-40 border-t border-gray-200 bg-white px-4 py-3 shadow-lg md:left-64"
          role="region"
          aria-label="Acciones de selección"
        >
          <div className="mx-auto flex max-w-6xl flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex flex-wrap items-center gap-3">
              <span className="text-sm font-medium text-gray-700">
                {selectedProductIds.size} seleccionado{selectedProductIds.size > 1 ? 's' : ''}
              </span>
              <button
                type="button"
                onClick={() =>
                  setSelectedProductIds(new Set(productosOrdenados.map((p) => p.id)))
                }
                className="text-sm font-medium text-blue-600 hover:text-blue-800"
              >
                Seleccionar todos
              </button>
            </div>
            <div className="flex flex-wrap items-center justify-end gap-2">
              <button
                type="button"
                onClick={() => setColeccionPickerOpen(true)}
                className="inline-flex items-center gap-2 rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700"
              >
                <span aria-hidden>🏷️</span> Agregar a colección
              </button>
              <button
                type="button"
                onClick={() => {
                  const ids = Array.from(selectedProductIds).join(',');
                  navigate(`/print-labels?products=${encodeURIComponent(ids)}`);
                }}
                className="inline-flex items-center rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
              >
                Imprimir etiquetas
              </button>
              <button
                type="button"
                onClick={() => {
                  setSelectedProductIds(new Set());
                  setSelectionMode(false);
                }}
                className="rounded-lg bg-gray-100 px-4 py-2 text-sm font-medium text-gray-800 hover:bg-gray-200"
              >
                Cancelar
              </button>
            </div>
          </div>
        </div>
      )}

      {currentEmpresa?.id && (
        <ColeccionPickerModal
          isOpen={coleccionPickerOpen}
          onClose={() => setColeccionPickerOpen(false)}
          empresaId={currentEmpresa.id}
          selectedProductIds={Array.from(selectedProductIds)}
          onSuccess={() => {
            setSelectedProductIds(new Set());
            setSelectionMode(false);
            setColeccionPickerOpen(false);
            void fetchProductos(currentEmpresa.id);
          }}
        />
      )}
    </div>
  );
};

export default GestionProductos;
